import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "@hooks/dark-mode";
import { setDocumentTitle } from "@hooks/document-title";
import {
  Book,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Search,
  Home,
  Leaf,
  FileText,
  BarChart3,
  Clock,
  Car,
  Package,
  HelpCircle
} from "lucide-react";
import { BsSun } from "react-icons/bs";
import LanguageSwitcher from "../../components/languageSwitch/LanguageSwitcher";

// Moon Icon
const MoonIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    aria-hidden="true"
    focusable="false"
    height={size}
    role="presentation"
    viewBox="0 0 24 24"
    width={size}
  >
    <path
      d="M21.53 15.93c-.16-.27-.61-.69-1.73-.49a8.46 8.46 0 01-1.88.13 8.409 8.409 0 01-5.91-2.82 8.068 8.068 0 01-1.44-8.66c.44-1.01.13-1.54-.09-1.76s-.77-.55-1.83-.11a10.318 10.318 0 00-6.32 10.21 10.475 10.475 0 007.04 8.99 10 10 0 002.89.55c.16.01.32.02.48.02a10.5 10.5 0 008.47-4.27c.67-.93.49-1.519.32-1.79z"
      fill="currentColor"
    />
  </svg>
);

// Category icons mapping
const categoryIcons: Record<string, React.ElementType> = {
  gettingStarted: Book,
  sustainability: Leaf,
  compliance: FileText,
  analytics: BarChart3,
  timeRegistration: Clock,
  drivingAllowance: Car,
  producerResponsibility: Package,
  faq: HelpCircle
};

interface KnowledgeBankLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export default function KnowledgeBankLayout({ children, title }: KnowledgeBankLayoutProps) {
  const { t } = useTranslation();
  const dark = useDarkMode();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(["gettingStarted"]);
  const [searchQuery, setSearchQuery] = useState("");

  React.useEffect(() => {
    if (title) {
      setDocumentTitle(title);
    }
  }, [title]);

  // Auto-expand the current category based on URL
  React.useEffect(() => {
    const pathParts = location.pathname.split("/");
    if (pathParts[1] === "knowledge-bank" && pathParts[2]) {
      const currentCategory = pathParts[2];
      if (!expandedCategories.includes(currentCategory)) {
        setExpandedCategories(prev => [...prev, currentCategory]);
      }
    }
  }, [location.pathname]);

  const titleColor = dark.dark ? "#ffffff" : "#111827";
  const textColor = dark.dark ? "#d1d5db" : "#4b5563";
  const accent = dark.dark ? "#60a5fa" : "#3b82f6";
  const borderColor = dark.dark ? "#374151" : "#e5e7eb";
  const sidebarBg = dark.dark ? "#111827" : "#f9fafb";
  const hoverBg = dark.dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)";

  // Knowledge bank navigation structure
  const categories = [
    {
      id: "gettingStarted",
      articles: ["introduction", "quickStart", "accountSetup"]
    },
    {
      id: "sustainability",
      articles: ["co2Tracking", "esgReporting", "emissionFactors"]
    },
    {
      id: "compliance",
      articles: ["csrd", "euTaxonomy", "ghgProtocol"]
    },
    {
      id: "timeRegistration",
      articles: ["setup", "mobileApp", "reports"]
    },
    {
      id: "drivingAllowance",
      articles: ["gpsTracking", "taxRules", "approval"]
    },
    {
      id: "producerResponsibility",
      articles: ["eprOverview", "packagingData", "feeCalculation"]
    },
    {
      id: "faq",
      articles: ["general", "technical", "billing"]
    }
  ];

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const isArticleActive = (categoryId: string, articleId: string) => {
    return location.pathname === `/knowledge-bank/${categoryId}/${articleId}`;
  };

  const isCategoryActive = (categoryId: string) => {
    return location.pathname.includes(`/knowledge-bank/${categoryId}`);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: dark.dark ? "#0f172a" : "#ffffff" }}>
      {/* Custom Knowledge Bank Navbar */}
      <nav
        className="h-16 px-4 flex items-center justify-between border-b fixed top-0 left-0 right-0 z-50"
        style={{
          backgroundColor: dark.dark ? "#111827" : "#ffffff",
          borderColor
        }}
      >
        <div className="flex items-center gap-4">
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {sidebarOpen ? (
              <X className="w-5 h-5" style={{ color: textColor }} />
            ) : (
              <Menu className="w-5 h-5" style={{ color: textColor }} />
            )}
          </button>

          {/* Logo - links to home */}
          <Link to="/" className="flex items-center gap-2">
            <img src={dark.getImage()} alt="Drivn" className="h-8" />
          </Link>

          {/* Separator */}
          <div className="hidden md:block h-6 w-px" style={{ backgroundColor: borderColor }} />

          {/* Knowledge Bank title */}
          <Link
            to="/knowledge-bank"
            className="hidden md:flex items-center gap-2 text-sm font-medium"
            style={{ color: accent }}
          >
            <Book className="w-4 h-4" />
            {t("knowledgeBank.title")}
          </Link>
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="hidden md:flex items-center relative">
            <Search className="w-4 h-4 absolute left-3" style={{ color: textColor }} />
            <input
              type="text"
              placeholder={t("knowledgeBank.search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2"
              style={{
                backgroundColor: dark.dark ? "#1f2937" : "#f3f4f6",
                borderColor,
                color: titleColor,
                width: "240px"
              }}
            />
          </div>

          {/* Back to main site */}
          <Link
            to="/"
            className="hidden md:flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:opacity-80 transition-all"
            style={{ color: textColor }}
          >
            <Home className="w-4 h-4" />
            {t("knowledgeBank.backToSite")}
          </Link>

          {/* Dark mode toggle */}
          <button
            onClick={dark.toggle}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {dark.dark ? <MoonIcon size={18} /> : <BsSun size={18} />}
          </button>

          {/* Language switcher */}
          <LanguageSwitcher variant="compact" />
        </div>
      </nav>

      <div className="flex flex-1 pt-16">
        {/* Sidebar */}
        <aside
          className={`
            fixed top-16 left-0 h-[calc(100vh-4rem)] w-72 overflow-y-auto border-r z-40
            transform transition-transform duration-200 ease-in-out
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          `}
          style={{
            backgroundColor: sidebarBg,
            borderColor
          }}
        >
          {/* Mobile search */}
          <div className="p-4 lg:hidden">
            <div className="flex items-center relative">
              <Search className="w-4 h-4 absolute left-3" style={{ color: textColor }} />
              <input
                type="text"
                placeholder={t("knowledgeBank.search")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: dark.dark ? "#1f2937" : "#ffffff",
                  borderColor,
                  color: titleColor
                }}
              />
            </div>
          </div>

          {/* Navigation */}
          <nav className="p-4">
            {categories.map((category) => {
              const Icon = categoryIcons[category.id] || Book;
              const isExpanded = expandedCategories.includes(category.id);
              const isActive = isCategoryActive(category.id);

              return (
                <div key={category.id} className="mb-2">
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className="w-full flex items-center justify-between p-2 rounded-lg text-sm font-medium transition-all hover:bg-gray-100 dark:hover:bg-gray-800"
                    style={{
                      backgroundColor: isActive ? `${accent}15` : "transparent",
                      color: isActive ? accent : titleColor
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      {t(`knowledgeBank.categories.${category.id}.title`)}
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="ml-6 mt-1 space-y-1 border-l-2 pl-2" style={{ borderColor: dark.dark ? "#374151" : "#e5e7eb" }}>
                      {category.articles.map((article) => {
                        const articleActive = isArticleActive(category.id, article);
                        return (
                          <Link
                            key={article}
                            to={`/knowledge-bank/${category.id}/${article}`}
                            onClick={() => setSidebarOpen(false)}
                            className="block p-2 text-sm rounded-lg transition-all hover:bg-gray-100 dark:hover:bg-gray-800"
                            style={{
                              backgroundColor: articleActive ? `${accent}15` : "transparent",
                              color: articleActive ? accent : textColor,
                              fontWeight: articleActive ? 500 : 400,
                              borderLeft: articleActive ? `2px solid ${accent}` : "2px solid transparent",
                              marginLeft: "-10px",
                              paddingLeft: "18px"
                            }}
                          >
                            {t(`knowledgeBank.categories.${category.id}.articles.${article}`)}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 lg:ml-72">
          {children}
        </main>
      </div>
    </div>
  );
}
